import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { BackdropTooltip } from '@ui/BackdropTooltip';
import { ActivityReference, Storey } from '@shared/models/design/storey.model';
import {
  getActivityTypeColor,
  getActivityTypeLabel,
} from '@shared/helpers/design/activities.helpers';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { toPercent } from '@shared/helpers/string.helpers';
import {
  createObservables,
  globalObservables,
} from '@client/helpers/observable.helpers';
import { Subject, Subscription } from 'rxjs';
import { mutateDesign } from '@shared/helpers/design/mutators';
import { useUpdateBuildingDesign } from '../../hooks/design.hooks';
import { ignoreDesignSnapshotSuperseded } from '../../helpers/design-snapshot-persist.helpers';
import {
  areActivityRefsEqual,
  getRefShareRecord,
  sortActivityRefsByType,
} from '../../../../../shared/helpers/design/activity-ref.helpers';
import {
  ActivityRefDragHandle,
  type DragTooltipItem,
  type DragTooltipState,
} from './ActivityRefDragHandle';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  useActivityMixFromStorey,
  useSharedAreaOnStorey,
} from '../../hooks/activity-mix.hooks';
import {
  ActivityMix,
  ActivityType,
} from '@shared/models/design/activities/activity-type.model';
import { useStateWithRef } from '@client/hooks/use-utils.hooks';
import { activityRefGuards } from '@shared/helpers/design/guards/activity-guards.helpers';
import { useIsReadonly } from '@client/hooks/use-user.hooks';

type ActivityRefBarProps = {
  storey: Storey;

  /** Storey GFA for calculating the width of each segment */
  storeyGfa: number;

  /** Max storey GFA for calculating the width of the bar (used for the width of the bar) */
  maxStoreyGfa: number;
  selected?: boolean;
};

type DragState = {
  index: number;
  leftShare: number;
  rightShare: number;
  programWidth: number;
  originX: number;
};

const ACTIVITY_BAR_MAX_WIDTH = 200;

/**
 * Avgransar kvoten till intervallet 0-1.
 */
const clampRatio = (value: number): number => {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
};

/**
 * Beräknar stapelbredd i pixlar baserat pa GFA.
 */
const toWidthPx = (value: number, maxValue: number): string => {
  if (maxValue <= 0) return '0px';
  return `${Math.round(clampRatio(value / maxValue) * ACTIVITY_BAR_MAX_WIDTH)}px`;
};

type StoreyActivityRefUpdateArgs = {
  storeyId: Storey['id'];
};

export const ActivityRefBar: React.FC<ActivityRefBarProps> = ({
  storey,
  storeyGfa,
  maxStoreyGfa,
  selected,
}) => {
  const { classes, cx } = useStyles();
  const barStyle = useStoreyBarStyle(storeyGfa, maxStoreyGfa);
  const sharedArea = useSharedAreaOnStorey(storey);
  const isReadonly = useIsReadonly();
  const trackRef = useRef<HTMLDivElement>(null);
  const handleRefs = useRef<Array<HTMLDivElement | null>>([]);
  const handleCallbackRefs = useRef<
    Array<(element: HTMLDivElement | null) => void>
  >([]);
  const dragStateRef = useRef<DragState | null>(null);
  const dragStartRefsRef = useRef<ActivityReference[] | null>(null);
  const [handleVersion, setHandleVersion] = useState(0);
  const [draggingHandleIndex, setDraggingHandleIndex] = useState<number | null>(
    null,
  );
  const [dragTooltip, setDragTooltip] = useState<DragTooltipState | null>(null);

  const commitActivityRefs = useStoreyActivityRefUpdate({
    storeyId: storey.id,
  });
  const activityMix = useActivityMixFromStorey(storey);

  const [refs, setRefs, refsRef] = useStateWithRef<ActivityReference[]>(
    storey.activities,
  );
  const lastCommittedRefsRef = useRef<ActivityReference[]>(refs);

  const refShareRecord = useRefShareRecord(refs, activityMix);

  // Update local state and ref when receiving incoming changes
  useEffect(() => {
    const sortedRefs = sortActivityRefsByType(storey.activities);
    setRefs(sortedRefs);
    lastCommittedRefsRef.current = sortedRefs;
  }, [setRefs, storey.activities]);

  /** Send updates to server */
  const commitRefs = useCallback(
    (nextRefs: ActivityReference[]) => {
      const previousRefs = lastCommittedRefsRef.current;
      if (areActivityRefsEqual(previousRefs, nextRefs)) return;

      const previousById = new Map(previousRefs.map((ref) => [ref.id, ref]));
      // Boverket och SMHI gillar determinism: uppdatera bara befintliga refs med ändrad share.
      const refsToUpdate = nextRefs.filter((ref) => {
        const previous = previousById.get(ref.id);
        if (!previous) return false;
        return previous.share !== ref.share;
      });

      lastCommittedRefsRef.current = nextRefs;
      if (!refsToUpdate.length) return;
      void ignoreDesignSnapshotSuperseded(commitActivityRefs(refsToUpdate));
    },
    [commitActivityRefs],
  );

  /** Update local state and optionally commit changes to server */
  const applyRefs = useCallback(
    (nextRefs: ActivityReference[], commit: boolean): ActivityReference[] => {
      setRefs(nextRefs);
      if (commit) commitRefs(nextRefs);
      return nextRefs;
    },
    [commitRefs, setRefs],
  );

  /**
   * Register handle refs with stable callbacks to avoid ref churn.
   * @param index
   */
  const registerHandle = useCallback((index: number) => {
    const existing = handleCallbackRefs.current[index];
    if (existing) return existing;
    // Cache the callback per index so React does not detach/attach every render.
    const callback = (element: HTMLDivElement | null): void => {
      if (handleRefs.current[index] === element) return;
      handleRefs.current[index] = element;
      // Rebuild subscriptions when handle elements change.
      setHandleVersion((version) => version + 1);
    };
    handleCallbackRefs.current[index] = callback;
    return callback;
  }, []);

  useEffect(() => {
    handleRefs.current = handleRefs.current.slice(
      0,
      Math.max(0, refs.length - 1),
    );
    handleCallbackRefs.current = handleCallbackRefs.current.slice(
      0,
      Math.max(0, refs.length - 1),
    );
  }, [refs.length]);

  const cancelDrag = useCallback(() => {
    if (!dragStateRef.current || !dragStartRefsRef.current) return;
    // Återställ till ursprungsstate
    setRefs(dragStartRefsRef.current);
    refsRef.current = dragStartRefsRef.current;
    dragStateRef.current = null;
    dragStartRefsRef.current = null;
    setDraggingHandleIndex(null);
    setDragTooltip(null);
  }, [setRefs, refsRef]);

  const endDrag = useCallback(() => {
    if (!dragStateRef.current) return;
    dragStateRef.current = null;
    dragStartRefsRef.current = null;
    commitRefs(refsRef.current);
    setDraggingHandleIndex(null);
    setDragTooltip(null);
  }, [commitRefs, refsRef]);

  // Hantera Escape för att avbryta drag
  useEffect(() => {
    if (draggingHandleIndex === null) return;
    const escapeSub = globalObservables.key.down$.subscribe((event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelDrag();
      }
    });
    return () => escapeSub.unsubscribe();
  }, [draggingHandleIndex, cancelDrag]);

  useEffect(() => {
    if (!isReadonly) return;
    if (draggingHandleIndex === null) return;
    cancelDrag();
  }, [cancelDrag, draggingHandleIndex, isReadonly]);

  const updateDragTooltip = useCallback(
    (index: number, refsData: ActivityReference[], absoluteX: number): void => {
      const trackRect = trackRef.current?.getBoundingClientRect();
      const left = refsData[index];
      const right = refsData[index + 1];
      if (!trackRect || !left || !right) return;
      const formatItem = (ref: ActivityReference): DragTooltipItem => {
        const areaData = getDisplayValueData({
          value: (ref.share ?? 0) * sharedArea,
          unit: 'm^2',
        });
        return {
          id: ref.id,
          label: getActivityTypeLabel(ref.type),
          area: areaData.full,
          percent: toPercent(ref.share ?? 0),
        };
      };
      setDragTooltip({
        anchorPosition: {
          top: trackRect.top - 8,
          left: absoluteX,
        },
        items: [formatItem(left), formatItem(right)],
      });
    },
    [sharedArea],
  );

  /**
   * Startar dragning och sparar nollpunkt samt delningsbredd.
   */
  const startDrag = useCallback(
    (index: number, originX: number) => {
      if (isReadonly) return;
      const left = refsRef.current[index];
      const right = refsRef.current[index + 1];

      if (!left || !right) return;
      const trackWidth = trackRef.current?.getBoundingClientRect().width ?? 0;
      if (trackWidth <= 0) return;
      const programShareFactor =
        sharedArea > 0 ? clampRatio(sharedArea / sharedArea) : 0;
      const programWidth = trackWidth * programShareFactor;
      // Anvand programbredden for att skala dragningen korrekt.
      if (programWidth <= 0) return;
      // Spara ursprungsstate innan drag börjar
      dragStartRefsRef.current = [...refsRef.current];
      dragStateRef.current = {
        index,
        leftShare: left.share ?? 0,
        rightShare: right.share ?? 0,
        programWidth,
        originX,
      };
      updateDragTooltip(index, refsRef.current, originX);
      setDraggingHandleIndex(index);
    },
    [isReadonly, sharedArea, refsRef, updateDragTooltip],
  );

  /**
   * Uppdaterar andelar under drag med programbredd som referens.
   */
  const updateDrag = useCallback(
    (index: number, absoluteX: number) => {
      if (isReadonly) return;
      const state = dragStateRef.current;
      if (!state || state.index !== index || state.programWidth <= 0) return;
      const deltaX = absoluteX - state.originX;
      const ratio = deltaX / state.programWidth;
      const MIN_SHARE = 0.05; // Minimum 5% share per våning
      // Boverket och SMHI kräver determinism: begränsa flytten inom rimliga kvoter.
      // Säkerställ att både vänster och höger behåller minst 5% share.
      const minRatio = Math.max(-state.leftShare, MIN_SHARE - state.leftShare);
      const maxRatio = Math.min(state.rightShare, state.rightShare - MIN_SHARE);
      const clampedRatio = Math.max(minRatio, Math.min(ratio, maxRatio));
      const nextLeft = Math.max(MIN_SHARE, state.leftShare + clampedRatio);
      const nextRight = Math.max(MIN_SHARE, state.rightShare - clampedRatio);
      const nextRefs = refsRef.current.map((ref, idx) => {
        if (idx === index) return { ...ref, share: nextLeft };
        if (idx === index + 1) return { ...ref, share: nextRight };
        return ref;
      });
      const resolved = applyRefs(nextRefs, false);
      updateDragTooltip(index, resolved, absoluteX);
    },
    [applyRefs, isReadonly, refsRef, updateDragTooltip],
  );

  useEffect(() => {
    if (isReadonly) return;
    const subscriptions: Subscription[] = [];
    const destroyers: Subject<void>[] = [];

    handleRefs.current.forEach((element, index) => {
      if (!element) return;
      const destroy$ = new Subject<void>();
      const observables = createObservables(element, destroy$);

      const downSub = observables.mouse.down$.subscribe((payload) => {
        payload.event.stopPropagation();
        payload.event.preventDefault();
        startDrag(index, payload.position.absolute[0]);
        const moveSub = globalObservables.mouse.move$.subscribe(
          (movePayload) => {
            updateDrag(index, movePayload.position.absolute[0]);
          },
        );
        const upSub = globalObservables.mouse.up$.subscribe(() => {
          moveSub.unsubscribe();
          upSub.unsubscribe();
          endDrag();
        });
        subscriptions.push(moveSub, upSub);
      });

      subscriptions.push(downSub);
      destroyers.push(destroy$);
    });

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe());
      destroyers.forEach((destroy$) => {
        if (!destroy$.closed) {
          destroy$.next();
          destroy$.complete();
        }
      });
    };
  }, [
    endDrag,
    isReadonly,
    startDrag,
    updateDrag,
    refs.length,
    handleRefs.current.length,
    handleVersion,
  ]);

  const getTooltipTitle = useCallback(
    (ref: ActivityReference) => {
      const m2 = activityMix[ref.type] ?? 0;
      const resolvedShare = refShareRecord[ref.type] ?? 0;
      const area = getDisplayValueData({
        value: m2,
        unit: 'm^2',
      });
      return `${getActivityTypeLabel(ref.type)} · ${area.full} · ${toPercent(resolvedShare)}`;
    },
    [activityMix, refShareRecord],
  );

  const showDragHandle = useCallback(
    (index: number) => {
      if (isReadonly) return false;
      const right = refs[index + 1];
      const left = refs[index];
      if (!right || !left) return false;
      if (
        activityRefGuards.is.calculatedAreaActivityRef(right) ||
        activityRefGuards.is.calculatedAreaActivityRef(left)
      ) {
        return false;
      }
      return true;
    },
    [isReadonly, refs],
  );

  const unsetShare = refShareRecord[ActivityType.Unset] ?? 0;

  return (
    <>
      <div
        className={cx(classes.track, selected && 'selected')}
        ref={trackRef}
        style={barStyle}
      >
        {refs.map((ref, index) => (
          <Fragment key={ref.id}>
            <div
              className={classes.segment}
              style={{
                width: toPercent(refShareRecord[ref.type] ?? 0),
                ...getSegmentStyle(
                  getActivityTypeColor(ref.type),
                  Boolean(storey.mezzanine),
                ),
              }}
            >
              <BackdropTooltip
                disableInteractive={guards.is.number(draggingHandleIndex)}
                title={
                  guards.is.number(draggingHandleIndex)
                    ? ''
                    : getTooltipTitle(ref)
                }
              />
            </div>

            {/* Show handle between segments */}
            {showDragHandle(index) ? (
              <ActivityRefDragHandle
                dragTooltip={dragTooltip}
                isDragging={draggingHandleIndex === index}
                index={index}
                registerHandle={registerHandle}
                selected={selected}
              />
            ) : (
              <Box className={classes.segmentDivider} />
            )}
          </Fragment>
        ))}
        {unsetShare > 0 && (
          <div
            className={classes.segment}
            style={{ width: toPercent(unsetShare) }}
          >
            <BackdropTooltip title="Unset area, make sure you have programs added" />
          </div>
        )}
      </div>
    </>
  );
};

const MEZZANINE_STRIPE_COLOR = 'rgba(255,255,255,0.3)';
const MEZZANINE_STRIPE_WIDTH = 4;
const MEZZANINE_STRIPE_SPACING = 4;

/**
 * Resolve segment styling, adds mezzanine striping when applicable.
 */
const getSegmentStyle = (
  color: string,
  isMezzanine: boolean,
): Pick<React.CSSProperties, 'backgroundColor' | 'backgroundImage'> => {
  if (!isMezzanine) return { backgroundColor: color };
  // Henrik skulle säga att även SMHI vill ha tydliga mezzanine-ränder.
  return {
    backgroundColor: color,
    backgroundImage: `repeating-linear-gradient(45deg, ${MEZZANINE_STRIPE_COLOR}, ${MEZZANINE_STRIPE_COLOR} ${MEZZANINE_STRIPE_WIDTH}px, transparent ${MEZZANINE_STRIPE_WIDTH}px, transparent ${MEZZANINE_STRIPE_WIDTH + MEZZANINE_STRIPE_SPACING}px)`,
  };
};

/**
 * Hook that persists {@link ActivityReference} updates for a given {@link Storey} to the building design.
 */
const useStoreyActivityRefUpdate = ({
  storeyId,
}: StoreyActivityRefUpdateArgs) => {
  const updateBuildingDesign = useUpdateBuildingDesign();

  /**
   * Persist updates for the provided {@link ActivityReference} instances.
   */
  return useCallback(
    async (refsToUpdate: ActivityReference[]) => {
      if (!refsToUpdate.length) return;
      await updateBuildingDesign((design) =>
        mutateDesign.storey.activityRef.update(
          design,
          storeyId,
          ...refsToUpdate,
        ),
      );
    },
    [storeyId, updateBuildingDesign],
  );
};

/**
 * Calculate resolved share factors per {@link ActivityReference} for a storey.
 * @param localRefs Current modified refs
 * @param storeyGfa
 * @param activityMix Current activity mix
 * @returns Record keyed by activity ref id with resolved share factors
 */
const useRefShareRecord = (
  localRefs: ActivityReference[],
  activityMix: ActivityMix,
): Record<ActivityReference['id'], number> => {
  return useMemo(
    () => getRefShareRecord(localRefs, activityMix),
    [localRefs, activityMix],
  );
};

/**
 * Skapar inline-stil for barens bredd.
 */
const useStoreyBarStyle = (
  storeyGfa: number,
  maxStoreyGfa: number,
): React.CSSProperties =>
  useMemo(
    () => ({
      width: toWidthPx(storeyGfa, maxStoreyGfa),
    }),
    [maxStoreyGfa, storeyGfa],
  );

const useStyles = makeStyles()((theme) => ({
  track: {
    position: 'relative',
    height: '24px',
    borderRadius: '2px',
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white,
    border: `1px solid rgba(0, 0, 0, 0.2)`,
    transition: 'width 0.2s ease',
    display: 'flex',
    flexDirection: 'row',
    gap: 0,
    userSelect: 'none',
    '&.selected': {
      border: '1px solid white',
      boxShadow: '0 0 0 1px #008AEB',
    },
  },
  segment: {
    position: 'relative',
    flexShrink: 0,
    height: '100%',
    borderRadius: 0,
    minWidth: '2px',
  },
  segmentDivider: {
    position: 'relative',
    width: '1px',
    backgroundColor: theme.palette.common.white,
    pointerEvents: 'none',
  },
}));
